import { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { upsertTeam } from '../../../core/assignment-rules';
import { hasPermission } from '../../../core/permissions';
import type { ScheduleEntry } from '../../../core/conflict-checker';
import {
  deleteTaskTeam,
  getAssignmentSchedule,
  getTaskTeams,
  saveTaskTeams,
} from '../../../db/assignment-store';
import type { TaskTeam } from '../../../types/assignment';
import type { Soldier, Vehicle } from '../../../types/fleet';
import type { Task } from '../../../types/task';
import type { User } from '../../../types/user';
import { TaskTeamsSection } from './TaskTeamsSection';

interface TaskTeamsModalProps {
  user: User;
  task: Task;
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  onClose: () => void;
  /**
   * Raised after every saved or dropped team, so the screen that opened the
   * dialog re-reads the convoy it is about to share as an image. The global
   * store bus says the same thing to every other screen; this is the direct
   * word to the host, and it is what makes "assign, close, share" produce a
   * picture of the convoy that was just assigned.
   */
  onSaved?: () => void | Promise<void>;
}

/**
 * The board's assignment dialog: the convoy of one saved task, edited against
 * the store as it happens. `ASSIGN_TASK` decides whether the dialog can change
 * anything — without it the crew is still readable, which is what a soldier
 * checking their own vehicle needs.
 */
export function TaskTeamsModal({
  user,
  task,
  vehicles,
  soldiers,
  onClose,
  onSaved,
}: TaskTeamsModalProps) {
  const canManage = hasPermission(user, 'ASSIGN_TASK');
  const [teams, setTeams] = useState<TaskTeam[]>([]);
  /** What every other open task has already booked — the overlap check reads it. */
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [loaded, booked] = await Promise.all([
      getTaskTeams(task.id),
      getAssignmentSchedule(task.id),
    ]);
    setTeams(loaded);
    setSchedule(booked);
  }, [task.id]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const [loaded, booked] = await Promise.all([
        getTaskTeams(task.id),
        getAssignmentSchedule(task.id),
      ]);
      if (!alive) return;
      setTeams(loaded);
      setSchedule(booked);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [task.id]);

  /** Every write goes through the store, then the dialog — and its host — re-read it. */
  const run = async (work: () => Promise<unknown>) => {
    setError(null);
    try {
      await work();
      await load();
      await onSaved?.();
    } catch (err) {
      setError(`הפעולה נכשלה — ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Modal
      title="צוותים ורכבים למשימה"
      subtitle={task.title}
      onClose={onClose}
      size="lg"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="h-10 px-5 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
        >
          סגירה
        </button>
      }
    >
      {loading ? (
        <p className="text-sm text-ink/50 py-6 text-center">טוען את שיבוץ הרכבים…</p>
      ) : (
        <div className="space-y-3">
          <TaskTeamsSection
            taskId={task.id}
            teams={teams}
            vehicles={vehicles}
            soldiers={soldiers}
            task={task}
            schedule={schedule}
            managerId={user.id}
            user={user}
            canManage={canManage}
            onSaveTeam={(team) => run(() => saveTaskTeams(user, task.id, upsertTeam(teams, team)))}
            onRemoveTeam={(team) => run(() => deleteTaskTeam(user, team.id))}
          />

          {!canManage ? (
            <p className="text-xs text-ink/40">נדרשת הרשאת שיבוץ משימה (ASSIGN_TASK) לעריכת הצוותים.</p>
          ) : null}

          {error ? (
            <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
